import { useCallback, useEffect, useRef, useState } from 'react';
import { certificateApi } from '../../services/certificateApi';

export const ACTIVE_BATCH_STORAGE_KEY = 'certificate.activeBatch';

const safeMessage = (error) =>
  typeof error?.message === 'string' && error.message.trim()
    ? error.message.trim()
    : 'No fue posible completar el lote de constancias';

const conflictRows = (error) => {
  if (error?.code !== 'DRAFT_CONFLICT') return [];
  const rows = error?.details?.conflictos;
  return Array.isArray(rows) ? rows : [];
};

const conflictLabel = (conflict) => {
  const name = String(conflict?.nombre_alumno || 'Alumno').trim();
  const batchId = Number(conflict?.id_lote);
  return Number.isInteger(batchId) && batchId > 0
    ? `${name} (lote ${batchId})`
    : name;
};

const defaultConfirmReplaceDrafts = (conflicts) => {
  const visible = conflicts.slice(0, 5).map((item) => `- ${conflictLabel(item)}`);
  const remaining = conflicts.length - visible.length;
  if (remaining > 0) {
    visible.push(`- y ${remaining} constancia${remaining === 1 ? '' : 's'} más`);
  }

  return window.confirm(
    [
      'Ya existe una constancia pendiente para uno o más alumnos de esta clase:',
      '',
      ...visible,
      '',
      '¿Deseas cancelar las versiones pendientes anteriores y generar estas nuevas constancias?',
      'Las constancias que ya estén publicadas no se cancelarán hasta que publiques la nueva versión.',
    ].join('\n'),
  );
};

const publicationConflictMessage = (conflicts) => {
  const visible = conflicts.slice(0, 3).map((conflict) => {
    const name = String(conflict?.nombre_alumno || 'Un alumno').trim();
    const newerBatch = Number(conflict?.id_lote_mas_reciente);
    return Number.isInteger(newerBatch) && newerBatch > 0
      ? `${name} tiene una versión más reciente en el lote ${newerBatch}`
      : `${name} tiene una versión más reciente`;
  });
  const remaining = conflicts.length - visible.length;
  const suffix = remaining > 0 ? ` y ${remaining} conflicto${remaining === 1 ? '' : 's'} más` : '';

  return `${visible.join('; ')}${suffix}. Quita la versión obsoleta de este lote antes de publicar.`;
};

const readStoredBatch = () => {
  try {
    const value = localStorage.getItem(ACTIVE_BATCH_STORAGE_KEY);
    if (!value) return null;
    const record = JSON.parse(value);
    if (
      !record ||
      typeof record !== 'object' ||
      typeof record.request_id !== 'string' ||
      !record.request_id.trim()
    ) {
      localStorage.removeItem(ACTIVE_BATCH_STORAGE_KEY);
      return null;
    }
    return record;
  } catch (_error) {
    localStorage.removeItem(ACTIVE_BATCH_STORAGE_KEY);
    return null;
  }
};

const storedBatchId = (record) => {
  const id = Number(record?.id_lote);
  return Number.isInteger(id) && id > 0 ? id : null;
};

const persistBatch = (record) => {
  localStorage.setItem(ACTIVE_BATCH_STORAGE_KEY, JSON.stringify(record));
};

const pendingCount = (batch) => {
  const count = Number(batch?.pendientes);
  return Number.isFinite(count) && count > 0 ? count : 0;
};

const defaultRequestId = () => {
  if (typeof window.crypto?.randomUUID !== 'function') {
    throw new Error('El navegador no puede crear un identificador seguro');
  }
  return window.crypto.randomUUID();
};

export const useCertificateBatch = ({
  api = certificateApi,
  createRequestId = defaultRequestId,
  confirmReplaceDrafts = defaultConfirmReplaceDrafts,
} = {}) => {
  const [batch, setBatch] = useState(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState('');
  const mountedRef = useRef(true);
  const operationRef = useRef(0);
  const runningRef = useRef(false);

  const isActive = useCallback(
    (operationId) =>
      mountedRef.current && operationId === operationRef.current,
    [],
  );

  const setBatchIfActive = useCallback(
    (operationId, nextBatch) => {
      if (isActive(operationId)) {
        setBatch(nextBatch);
      }
    },
    [isActive],
  );

  const processUntilSettled = useCallback(
    async (
      idLote,
      operationId,
      initialBatch = null,
      probeProcessing = false,
    ) => {
      let next = initialBatch || (await api.getBatch(idLote));
      setBatchIfActive(operationId, next);

      if (
        probeProcessing &&
        isActive(operationId) &&
        pendingCount(next) === 0 &&
        next?.estado === 'procesando'
      ) {
        next = await api.processChunk(idLote);
        setBatchIfActive(operationId, next);
      }

      while (isActive(operationId) && pendingCount(next) > 0) {
        next = await api.processChunk(idLote);
        setBatchIfActive(operationId, next);
      }
      return next;
    },
    [api, isActive, setBatchIfActive],
  );

  const beginOperation = useCallback(() => {
    const operationId = operationRef.current + 1;
    operationRef.current = operationId;
    runningRef.current = true;
    setRunning(true);
    setError('');
    return operationId;
  }, []);

  const finishOperation = useCallback(
    (operationId) => {
      if (isActive(operationId)) {
        runningRef.current = false;
        setRunning(false);
      }
    },
    [isActive],
  );

  const start = useCallback(
    async (payload) => {
      if (runningRef.current) return null;

      const stored = readStoredBatch();
      if (storedBatchId(stored)) {
        setError('Ya existe un lote activo. Reanúdalo o crea otro lote.');
        return null;
      }

      const operationId = beginOperation();
      try {
        const requestId = stored?.request_id || createRequestId();
        persistBatch({ request_id: requestId, id_lote: null });
        const createPayload = {
          ...payload,
          request_id: requestId,
        };

        const createBatchRequest =
          typeof api.createBatchSafe === 'function'
            ? api.createBatchSafe
            : api.createBatch;
        let created;
        try {
          created = await createBatchRequest(createPayload);
        } catch (requestError) {
          const conflicts = conflictRows(requestError);
          if (!conflicts.length) {
            throw requestError;
          }

          const replace = await confirmReplaceDrafts(conflicts);
          if (!replace) {
            if (isActive(operationId)) {
              setError(
                'No se generó el lote. Las constancias pendientes anteriores se conservaron.',
              );
            }
            return null;
          }

          created = await createBatchRequest({
            ...createPayload,
            reemplazar_borradores: true,
          });
        }

        const idLote = Number(created?.id_lote);
        if (!Number.isInteger(idLote) || idLote <= 0) {
          throw new Error('El servidor no devolvió un lote válido');
        }
        persistBatch({ request_id: requestId, id_lote: idLote });
        return await processUntilSettled(idLote, operationId, created);
      } catch (requestError) {
        if (isActive(operationId)) {
          setError(safeMessage(requestError));
        }
        return null;
      } finally {
        finishOperation(operationId);
      }
    },
    [
      api,
      beginOperation,
      confirmReplaceDrafts,
      createRequestId,
      finishOperation,
      isActive,
      processUntilSettled,
    ],
  );

  const resume = useCallback(async () => {
    if (runningRef.current) return null;
    const idLote = storedBatchId(readStoredBatch()) || Number(batch?.id_lote);
    if (!Number.isInteger(idLote) || idLote <= 0) {
      setError('No hay un lote pendiente para reanudar');
      return null;
    }

    const operationId = beginOperation();
    try {
      return await processUntilSettled(idLote, operationId, null, true);
    } catch (requestError) {
      if (isActive(operationId)) {
        setError(safeMessage(requestError));
      }
      return null;
    } finally {
      finishOperation(operationId);
    }
  }, [
    batch,
    beginOperation,
    finishOperation,
    isActive,
    processUntilSettled,
  ]);

  const retryFailed = useCallback(async () => {
    if (runningRef.current) return null;
    const idLote = storedBatchId(readStoredBatch()) || Number(batch?.id_lote);
    if (!Number.isInteger(idLote) || idLote <= 0) {
      setError('No hay un lote con errores para reintentar');
      return null;
    }

    const operationId = beginOperation();
    try {
      const reopened = await api.retryFailed(idLote);
      setBatchIfActive(operationId, reopened);
      return await processUntilSettled(idLote, operationId, reopened);
    } catch (requestError) {
      if (isActive(operationId)) {
        setError(safeMessage(requestError));
      }
      return null;
    } finally {
      finishOperation(operationId);
    }
  }, [
    api,
    batch,
    beginOperation,
    finishOperation,
    isActive,
    processUntilSettled,
    setBatchIfActive,
  ]);

  const load = useCallback(
    async (record) => {
      if (runningRef.current) return null;
      const idLote = Number(record?.id_lote ?? record);
      if (!Number.isInteger(idLote) || idLote <= 0) {
        setError('El lote seleccionado no es válido');
        return null;
      }

      const operationId = beginOperation();
      try {
        const loaded = await api.getBatch(idLote);
        if (loaded?.estado_publicacion === 'borrador') {
          const requestId = loaded.request_id || record?.request_id;
          if (typeof requestId === 'string' && requestId.trim()) {
            persistBatch({ request_id: requestId, id_lote: idLote });
          }
        }
        setBatchIfActive(operationId, loaded);
        return loaded;
      } catch (requestError) {
        if (isActive(operationId)) {
          setError(safeMessage(requestError));
        }
        return null;
      } finally {
        finishOperation(operationId);
      }
    },
    [api, beginOperation, finishOperation, isActive, setBatchIfActive],
  );

  const publish = useCallback(async () => {
    if (runningRef.current) return null;
    const idLote = storedBatchId(readStoredBatch()) || Number(batch?.id_lote);
    if (!Number.isInteger(idLote) || idLote <= 0) {
      setError('No hay un lote listo para publicar');
      return null;
    }

    const operationId = beginOperation();
    try {
      if (typeof api.listPublicationConflicts === 'function') {
        const conflicts = await api.listPublicationConflicts(idLote);
        if (Array.isArray(conflicts) && conflicts.length > 0) {
          if (isActive(operationId)) {
            setError(publicationConflictMessage(conflicts));
          }
          return null;
        }
      }

      const published = await api.publishBatch(idLote);
      localStorage.removeItem(ACTIVE_BATCH_STORAGE_KEY);
      setBatchIfActive(operationId, published);
      return published;
    } catch (requestError) {
      if (isActive(operationId)) {
        setError(safeMessage(requestError));
      }
      return null;
    } finally {
      finishOperation(operationId);
    }
  }, [api, batch, beginOperation, finishOperation, isActive, setBatchIfActive]);

  const cancel = useCallback(async () => {
    if (runningRef.current) return null;
    const idLote = storedBatchId(readStoredBatch()) || Number(batch?.id_lote);
    if (!Number.isInteger(idLote) || idLote <= 0) {
      setError('No hay un lote pendiente para cancelar');
      return null;
    }

    const operationId = beginOperation();
    try {
      const cancelled = await api.cancelBatch(idLote);
      localStorage.removeItem(ACTIVE_BATCH_STORAGE_KEY);
      setBatchIfActive(operationId, cancelled);
      return cancelled;
    } catch (requestError) {
      if (isActive(operationId)) {
        setError(safeMessage(requestError));
      }
      return null;
    } finally {
      finishOperation(operationId);
    }
  }, [api, batch, beginOperation, finishOperation, isActive, setBatchIfActive]);

  const cancelDraftIssuance = useCallback(
    async (idEmision) => {
      if (runningRef.current) return null;
      const idLote = storedBatchId(readStoredBatch()) || Number(batch?.id_lote);
      const issuanceId = Number(idEmision);
      if (
        !Number.isInteger(idLote) ||
        idLote <= 0 ||
        !Number.isInteger(issuanceId) ||
        issuanceId <= 0
      ) {
        setError('La constancia seleccionada no es válida');
        return null;
      }

      const operationId = beginOperation();
      try {
        const updated = await api.cancelDraftIssuance(idLote, issuanceId);
        setBatchIfActive(operationId, updated);
        return updated;
      } catch (requestError) {
        if (isActive(operationId)) {
          setError(safeMessage(requestError));
        }
        return null;
      } finally {
        finishOperation(operationId);
      }
    },
    [api, batch, beginOperation, finishOperation, isActive, setBatchIfActive],
  );

  const clear = useCallback(() => {
    operationRef.current += 1;
    runningRef.current = false;
    localStorage.removeItem(ACTIVE_BATCH_STORAGE_KEY);
    setBatch(null);
    setError('');
    setRunning(false);
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    const stored = readStoredBatch();
    const idLote = storedBatchId(stored);
    if (!idLote) {
      return () => {
        mountedRef.current = false;
        operationRef.current += 1;
      };
    }

    const operationId = operationRef.current + 1;
    operationRef.current = operationId;
    api
      .getBatch(idLote)
      .then((restored) => {
        if (restored?.estado_publicacion !== 'borrador') {
          localStorage.removeItem(ACTIVE_BATCH_STORAGE_KEY);
        }
        setBatchIfActive(operationId, restored);
      })
      .catch((requestError) => {
        if (isActive(operationId)) {
          setError(safeMessage(requestError));
        }
      });

    return () => {
      mountedRef.current = false;
      operationRef.current += 1;
      runningRef.current = false;
    };
  }, [api, isActive, setBatchIfActive]);

  return {
    batch,
    running,
    error,
    start,
    load,
    resume,
    retryFailed,
    publish,
    cancel,
    cancelDraftIssuance,
    clear,
  };
};
